import { resolvedSlug } from './resolve';
import { getMissingTenantEnvKeys } from './validate';
import { tenantRegistry, paletteRegistry, assetsRegistry, svgAssetsRegistry, copyOverlayRegistry } from './registry';
import { neutralPalette, neutralTenantConfig, neutralAssets, neutralSvgAssets } from './neutral';
import { FrontendTenantConfig, TenantAssets, TenantPalette, TenantSvgAssets } from './types';

export type TenantError = 'unknown-host' | { missingEnv: string[] };

const resolvedTenant = resolvedSlug ? tenantRegistry[resolvedSlug] : undefined;
const missingEnv = resolvedTenant ? getMissingTenantEnvKeys(resolvedTenant.slug) : [];

/** The tenant for this page load, or `null` on an unknown hostname / failed D7 env validation
 *  (fail-closed) — the boot path (phase 5) renders the neutral screen instead of the app when this
 *  is `null`. */
export const activeTenant: FrontendTenantConfig | null =
  resolvedTenant && missingEnv.length === 0 ? resolvedTenant : null;

/** Why `activeTenant` is `null`; `null` itself when resolution succeeded. */
export const tenantError: TenantError | null = (() => {
  if (!resolvedTenant) {
    return 'unknown-host';
  }

  return missingEnv.length > 0 ? { missingEnv } : null;
})();

if (tenantError) {
  // Names the missing key NAMES only, never their values (D7) — never log a secret/id.
  console.error(
    'Tenant resolution failed:',
    tenantError === 'unknown-host' ? 'unknown hostname' : `missing env keys: ${tenantError.missingEnv.join(', ')}`,
  );
}

// theme.tsx and the PDF stylesheets read tenant state at module-evaluation time regardless of
// whether resolution succeeded (D6), so they need a defined value even when `activeTenant` is
// null. That fallback is deliberately NEUTRAL, never "the first registered tenant": the SPEC
// forbids any tenant name/logo/asset/id rendering on an unknown hostname, and a real-tenant
// fallback would leak one brand onto another's hostname *plausibly* — hiding a boot-wiring bug
// instead of exposing it. Grey leaks nothing and makes the bug obvious on sight. See ./neutral.ts.

/** Always-defined tenant config for module-scope consumers (theme, PDF stylesheets) that cannot
 *  render conditionally on `activeTenant` being non-null (D6). Neutral when resolution failed. */
export const resolvedTenantConfig: FrontendTenantConfig = activeTenant ?? neutralTenantConfig;

/** Always-defined resolved palette for the same reason — see `resolvedTenantConfig` above. */
export const palette: TenantPalette = activeTenant ? paletteRegistry[activeTenant.slug] : neutralPalette;

/** Phase 3 (D5) — the resolved tenant's synchronous asset map (URL strings). Neutral (empty
 *  strings) on an unknown hostname / failed validation, never a real tenant's fallback — see
 *  `./neutral.ts`. */
export const assets: TenantAssets = activeTenant ? assetsRegistry[activeTenant.slug] : neutralAssets;

/** D4 — the resolved tenant's statically-imported `ReactComponent` SVGs (`icon-lock.svg` only). */
export const svgAssets: TenantSvgAssets = activeTenant ? svgAssetsRegistry[activeTenant.slug] : neutralSvgAssets;

/** Phase 4 (D8) — the resolved tenant's i18n copy overlay, or `undefined` on an unknown hostname /
 *  failed validation. Deliberately `undefined`, not a neutral overlay: the neutral screen renders
 *  from base-namespace keys only (G5), so there is nothing tenant-specific to overlay when
 *  `activeTenant` is `null` — unlike `palette`/`assets`/`svgAssets`, nothing at module scope reads
 *  this before boot, so it does not need an always-defined fallback (D6 does not apply here). */
export const copyOverlay: unknown = activeTenant ? copyOverlayRegistry[activeTenant.slug] : undefined;

/** SAA-61/05 R8 — the resolved tenant's status workflow, beside `resolvedTenantConfig` for the
 *  same reason (D6): read at module scope by consumers that cannot render conditionally. Neutral
 *  (empty arrays) on a failed resolution, matching every other field here. */
export const { workflow } = resolvedTenantConfig;

/** SAA-61/05 R8 — the one affirmative-polarity helper every FE gate routes through
 *  (`editable ⇒ enabled`). Fails closed to `false` on a missing/null status or a failed
 *  resolution (`workflow.editableStatuses` is `[]` on the neutral tenant). */
export const isEditableStatus = (status?: string | null): boolean =>
  !!status && workflow.editableStatuses.includes(status);
